// toast-container.component.ts
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ToastRef } from '@shared/models';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-stack" role="region" aria-label="Notifications">
      @for (toast of toasts$ | async; track trackById($index, toast)) {
        <div class="toast"
             [attr.data-type]="toast.options.type"
             role="status"
             aria-live="polite">
  
          <div class="toast-main">
            @if(toast.options.title) {
              <div class="toast-title">{{ toast.options.title }}</div>
            }
            <div class="toast-message">{{ toast.message }}</div>
          </div>
  
          @if (toast.options.actionText) {
            <button class="toast-action"
                    type="button"
                    (click)="action(toast.id)">
              {{ toast.options.actionText }}
            </button>
          }
  
          @if(toast.options.dismissible) {
            <button class="toast-close"
                    type="button"
                    aria-label="Dismiss"
                    (click)="dismiss(toast.id)">
              ✕
            </button>
          }
  
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-stack {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: min(360px, calc(100vw - 24px));
      padding: 12px;
      box-sizing: border-box;
    }

    .toast {
      display: grid;
      grid-template-columns: 1fr auto auto;
      align-items: center;
      gap: 10px;
      padding: 12px 12px;
      border-radius: 10px;
      background: #1f232a;
      color: #fff;
      box-shadow: 0 8px 20px rgba(0,0,0,.25);
      border: 1px solid rgba(255,255,255,.08);
    }

    .toast[data-type="success"] { border-left: 10px solid #2ecc71; }
    .toast[data-type="info"]    { border-left: 10px solid #3498db; }
    .toast[data-type="warning"] { border-left: 10px solid #f1c40f; }
    .toast[data-type="error"]   { border-left: 10px solid #e74c3c; }

    .toast-title { font-weight: 600; margin-bottom: 2px; }
    .toast-message { opacity: .95; }

    .toast-action, .toast-close {
      background: transparent;
      border: 0;
      color: #fff;
      opacity: .9;
      cursor: pointer;
      padding: 6px 8px;
      border-radius: 8px;
    }
    .toast-action:hover, .toast-close:hover { background: rgba(255,255,255,.08); opacity: 1; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastContainerComponent {
  private _toasts = new BehaviorSubject<ToastRef[]>([]);
  readonly toasts$ = this._toasts.asObservable();

  // Called by the service
  setToasts(list: ToastRef[]) { this._toasts.next(list); }

  // Hooked up by the service
  onDismiss?: (id: string) => void;
  onAction?: (id: string) => void;

  dismiss(id: string) { this.onDismiss?.(id); }
  action(id: string) { this.onAction?.(id); }

  trackById = (_: number, t: ToastRef) => t.id;
}